/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer, useRef, useMemo, useCallback } from "react"
import { useOutletContext, useParams, useNavigate } from "react-router-dom"
import { isAxiosError } from "axios"
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError"
import { CheckCircle, CornerDownLeft, Info, CircleX, Download, Pencil, PencilOff, Trash } from "lucide-react"
import { BarLoader, PulseLoader } from "react-spinners"

import { apiURL } from "@/services/apiService"
import { useAuth } from "@/services/userService"
import { useGetForum } from "@/services/forumService"
import { useGetTopic, useDeleteTopic } from "@/services/topicService"
import { useGetMessagesSWR, useUpdateMessage, useDeleteMessage, useCreateMessage, useUploadFile, useDeleteFile } from "@/services/messageService"
import ForumHeader from "@/components/ForumHeader"
import ErrorMessage from "@/components/ErrorMessage"
import { toInitials } from "@/utils/toInitials"

/* MDX plugins */
import { headingsPlugin, quotePlugin, listsPlugin, thematicBreakPlugin, tablePlugin, markdownShortcutPlugin, toolbarPlugin, MDXEditor } from '@mdxeditor/editor';
import { linkPlugin, linkDialogPlugin, diffSourcePlugin, imagePlugin, codeBlockPlugin, codeMirrorPlugin } from '@mdxeditor/editor'

import '@mdxeditor/editor/style.css';
import './editor.css'

/* MDX toolbar */
import { BlockTypeSelect, BoldItalicUnderlineToggles, StrikeThroughSupSubToggles, CodeToggle, CreateLink } from '@mdxeditor/editor';
import { DiffSourceToggleWrapper, InsertCodeBlock, InsertTable, ListsToggle, ConditionalContents } from '@mdxeditor/editor';
import { UndoRedo, InsertThematicBreak, Separator, InsertImage, ChangeCodeMirrorLanguage} from '@mdxeditor/editor';


/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)


const initialState = {
    forum: null,
    topic: null,
    apiPayload: {
        content: "",
        status: "draft"
    },
    errorMessage: "",
    attachmentLoading: false,
    isLoading: false,

    draft: null,
    draftHandlerMessage: {
        type: "",
        message: ""
    },

    forumIsLoading: false,
    topicIsLoading: false,
    updateIsLoading: false,
    uploadIsLoading: false,

}



const ACTIONS = {
    SET_FORUM: "setForum",
    SET_TOPIC: "setTopic",
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",

    SET_API_PAYLOAD: "setapiPayload",
    SET_DRAFT: "setDraft",
    
    SET_DRAFT_HANDLER_MESSAGE: "setDraftHandlerMessage",
    ADD_DRAFT_ATTACHMENT: "addDraftAttachment",
    DELETE_DRAFT_ATTACHMENT: "deleteDraftAttachment",

    SET_FORUM_IS_LOADING: "setForumIsLoading",
    SET_TOPIC_IS_LOADING: "setTopicIsLoading",
    SET_UPDATE_IS_LOADING: "setUpdateIsLoading",
    SET_UPLOAD_IS_LOADING: "setUploadIsLoading",

}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_FORUM:
            return { ...state, forum: action.payload }
        case ACTIONS.SET_TOPIC:
            return { ...state, topic: action.payload }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_API_PAYLOAD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload } }
        case ACTIONS.SET_DRAFT:
            return { ...state, draft: action.payload }
        case ACTIONS.SET_DRAFT_HANDLER_MESSAGE:
            return { ...state, draftHandlerMessage: action.payload }
        case ACTIONS.SET_FORUM_IS_LOADING:
            return { ...state, forumIsLoading: action.payload }
        case ACTIONS.SET_TOPIC_IS_LOADING:
            return { ...state, topicIsLoading: action.payload }
        case ACTIONS.SET_UPDATE_IS_LOADING:
            return { ...state, updateIsLoading: action.payload }
        case ACTIONS.SET_UPLOAD_IS_LOADING:
            return { ...state, uploadIsLoading: action.payload }
        case ACTIONS.ADD_DRAFT_ATTACHMENT:
            return { ...state, draft: { ...state.draft, attachments: [...state.draft.attachments, action.payload] } }
        case ACTIONS.DELETE_DRAFT_ATTACHMENT:
            return { ...state, draft: { ...state.draft, attachments: state.draft.attachments.filter((attachment) => attachment.uuid != action.payload) } }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {

    const {forum_id, id} = useParams()
    const { auth } = useAuth()
    const navigate = useNavigate()

    const getForum = useGetForum()
    const getTopic = useGetTopic()
    const deleteTopic = useDeleteTopic()
    const createMessage = useCreateMessage()
    const updateMessage = useUpdateMessage()
    const deleteMessage = useDeleteMessage()
    const { uploadFile, progress } = useUploadFile()
    const deleteFile = useDeleteFile()
    const { data: messages, 
        isLoading: isLoadingMessages, 
        isValidating: isValidatingMessages, 
        error: errorMessages,
        callToAction: mutateMessages
    } = useGetMessagesSWR(forum_id, id)

    const [ , setPageTitle,  , , , setShowTitle] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const editorRef = useRef(null)
    const editorDivRef = useRef(null)

    const messageRefs = useRef({})

    /* On supprime le titre */
    useEffect(() => {
        setPageTitle()
        setShowTitle(false)

        return () => {
            setPageTitle("Titre")
            setShowTitle(true)
        }
    }, [setPageTitle, setShowTitle])



    /* On change le titre quand le topic est récupéré */
    useEffect(() => {
        if (state?.topic != null) {
            setPageTitle(state?.topic?.title)
        }
    }, [state?.topic])



    /* Récupération des informations du forum */
    useEffect(() => {
        const localGetForum = async () => {
            dispatch({ type: ACTIONS.SET_FORUM_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const forum = await getForum(forum_id)
                dispatch({ type: ACTIONS.SET_FORUM, payload: forum });

            } catch (error) {
                console.error('forums/[forum_id]::getForum::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_FORUM_IS_LOADING, payload: false });
            }
        }

        localGetForum()
    }, [forum_id])



    /* Récupération du sujet */
    useEffect(() => {
        const localGetTopic = async () => {
            dispatch({ type: ACTIONS.SET_TOPIC_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const topic = await getTopic(forum_id, id)
                dispatch({ type: ACTIONS.SET_TOPIC, payload: topic });

            } catch (error) {
                console.error('forums/[forum_id]/topics/[id]::getTopic::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_TOPIC_IS_LOADING, payload: false });
            }
        }

        if (forum_id && id) {
            localGetTopic()
        }
    }, [forum_id, id])



    /* On rempli le draft si il existe à la récupération des messages */
    useEffect(() => {
        if (messages) {
            const draft = messages.find((message) => message.status == "draft")
            
            if (draft && state.draft === null) {
                dispatch({ type: ACTIONS.SET_DRAFT, payload: draft })
                dispatch({ type: ACTIONS.SET_API_PAYLOAD, payload: draft.content, subtype: "content" })
            }

            /* Si les messages sont déjà affichés, on les met a jour */
            messages.map((message) => {
                if (messageRefs.current[message.id]) {
                    messageRefs.current[message.id].setMarkdown(message.content)
                }
            })
        }
    }, [messages])



    /* On gere un etat commun de chargement */
    useEffect(() => {
        dispatch({
            type: ACTIONS.SET_IS_LOADING, 
            payload: state.forumIsLoading || state.topicIsLoading || isLoadingMessages || state.updateIsLoading || isValidatingMessages || state.uploadIsLoading
        })
    }, [isLoadingMessages, isValidatingMessages, state.forumIsLoading, state.topicIsLoading, state.updateIsLoading, state.uploadIsLoading])



    /* On gere un etat commun d'erreur */
    useEffect(() => {
        if (errorMessages)
            dispatch({type: ACTIONS.SET_ERROR_MESSAGE, payload: errorMessages.message})
    }, [errorMessages])



    /* Lorsque le draft est modifié, on le refocus */
    useEffect(() => {
        if (!state.draft || !editorDivRef.current) return;
    
        const timer = setTimeout(() => {
            const top = editorDivRef.current.offsetTop;
            if (top > 0) {
                window.scrollTo({ top, behavior: 'smooth' });
            }
        }, 500); // délai à ajuster si besoin
    
        return () => clearTimeout(timer);
    }, [state.draft, editorDivRef]);



    /* Determine si on a le droit de créer un message */
    const hasTopicWriteRight = useMemo(() => {
        let result = false

        state?.forum?.rights?.map((right) => {
            if( 
                (
                    (right.right == "topic_create" || right.right == "forum_write" ) && ( ! state?.topic?.no_response ) && (state?.forum?.competition?.active)
                ) || (
                    (right.right == "topic_create" ) && ( state?.topic?.author?.username == auth?.user?.username ) && (state?.forum?.competition?.active)
                ) || (
                    right.right == "forum_moderate"
                )
            ) {
                result = true
            }
        })
        return result

    }, [state?.forum, auth.user, state.topic])



    /* Determine si on a le droit de modérer le forum, et aussi de supprimer un sujet existant */
    const hasTopicModeratorRight = useMemo(() => {
        let result = false

        state?.forum?.rights?.map((right) => {
            if(right.right == "forum_moderate") {
                result = true
            }
        })
        return result

    }, [state?.forum])



    /* Détermine si pour un message donnée, on a le droit de l'éditer */
    const hasMessageWriteRight = useCallback((message) => {
        
        if (message.author.username == auth?.user?.username )
            return true

        let result = false

        state?.forum?.rights?.map((right) => {
            if(right.right == "forum_moderate") {
                result = true
            }
        })

        return result

    }, [state?.forum, auth])



    /* Lorsqu'on clique sur le bouton Enregistrer */
    const handleSaveDraft = async () => {
        const payload = {
            content: editorRef.current?.getMarkdown(),
            status: "draft"
        }

        if (payload.content == state.draft.content) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "info", message: "Aucun changement a enregistrer"} })
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 2000)
            return
        }
        if (payload.content.length >= 65536) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Le message est trop long (max 65536 caractères)"} })
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
            return
        }

        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });

            const new_draft = await updateMessage(forum_id, id, state.draft.id, payload)
            
            dispatch({type: ACTIONS.SET_DRAFT, payload: new_draft})
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le brouillon a été enregistré"} })
            mutateMessages()

        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleSaveDraft::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
        }
    }



    /* Lorsqu'on clique sur le bouton Enregistrer et publier */
    const handlePublishDraft = async () => {
        const payload = {
            content: editorRef.current?.getMarkdown(),
            status: "published"
        }

        if (payload.content.trim().length == 0) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Un message vide n'est pas autorisé"}})
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 2000)
            return
        }
        if (payload.content.length >= 65536) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Le message est trop long (max 65536 caractères)"} })
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
            return
        }

        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });

            await updateMessage(forum_id, id, state.draft.id, payload)

            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le message a été publié"}})
            
            
            dispatch({type: ACTIONS.SET_DRAFT, payload: null})
            mutateMessages()

            const topic = await getTopic(forum_id, id)
            dispatch({type: ACTIONS.SET_TOPIC, payload: topic})

        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleSaveDraft::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
        }
    }



    /* Lorsqu'on clique sur le bouton Enregistrer le message */
    const handleSaveMessage = async () => {
        const payload = {
            content: editorRef.current?.getMarkdown(),
            status: state.draft.status
        }

        if (payload.content.trim().length == 0) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Un message vide n'est pas autorisé"}})
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 2000)
            return
        }
        if (payload.content.length >= 65536) {
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Le message est trop long (max 65536 caractères)"} })
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
            return
        }

        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });

            await updateMessage(forum_id, id, state.draft.id, payload)

            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le message a été sauvegardé"}})
            
            dispatch({type: ACTIONS.SET_DRAFT, payload: null})
            mutateMessages()

        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleSaveDraft::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
        }
    }



    /* Lorsqu'on clique sur le bouton Supprimer le brouillon */
    const handleDeleteDraft = async () => {
        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });

            await deleteMessage(forum_id, id, state.draft.id)

            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le brouillon a été supprimé"}})            
            
            dispatch({type: ACTIONS.SET_DRAFT, payload: null})
            mutateMessages()

        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleSaveDraft::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 3000)
        }
    }



    /* Lorsqu'on clique sur le bouton Répondre */
    const newDraft = async (content) => {
        const payload = {
            content: content ?? "",
            status: "draft"
        }

        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });
            const draft = await createMessage(forum_id, id, payload)

            dispatch({type: ACTIONS.SET_DRAFT, payload: draft})
        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::newDraft::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
        }
    }



    /* Handler de l'upload d'image */
    const imageUploadHandler = async (file) => {
        try {
            dispatch({ type: ACTIONS.SET_UPLOAD_IS_LOADING, payload: true });

            if (file.size > 1024 * 1024 * 20) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Le fichier est trop volumineux, la taille maximale autorisée est de 20 Mo"} });
                return null
            }

            const response = await uploadFile(forum_id, id, state.draft.id, file, true)
            
            if (response.url)
                return `${apiURL}${response.url}`
            else
                return null
        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::imageUploadHandler::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPLOAD_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 5000)
        }

    }



    /* Handler de l'upload de fichier */
    const fileUploadHandler = async (file) => {
        try {
            dispatch({ type: ACTIONS.SET_UPLOAD_IS_LOADING, payload: true });

            if (file.size > 1024 * 1024 * 20) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: "Le fichier est trop volumineux, la taille maximale autorisée est de 20 Mo"} });
                return
            }

            const response = await uploadFile(forum_id, id, state.draft.id, file, false)
            
            if (response) {
                dispatch({type: ACTIONS.ADD_DRAFT_ATTACHMENT, payload: response})
                dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le fichier a été ajouté au brouillon"}})
            }
        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::imageUploadHandler::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPLOAD_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 5000)
        }

    }



    /* Handler de la suppression d'une pièce jointe */
    const handleDeleteFile = async (uuid) => {
        try {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: true });
            await deleteFile(forum_id, id, state.draft.id, uuid)
            dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "success", message: "Le fichier a été supprimé"}})
            dispatch({type: ACTIONS.DELETE_DRAFT_ATTACHMENT, payload: uuid})
        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleDeleteAttachment::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: axiosErrorToStringError(error)} });
            } else {
                dispatch({ type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "error", message: error?.message || "Erreur inconnue"} });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_UPDATE_IS_LOADING, payload: false });
            setTimeout(() => { dispatch({type: ACTIONS.SET_DRAFT_HANDLER_MESSAGE, payload: {type: "", message: ""} })}, 5000)
        }
    }



    /* Handler de la suppression d'un sujet */
    const handleDeleteTopic = async () => {
        try {
            dispatch({ type: ACTIONS.SET_TOPIC_IS_LOADING, payload: true });
            await deleteTopic(forum_id, id)
            
            navigate(`/forums/${forum_id}`)
        } catch (error) {
            console.error('forums/[forum_id]/topics/[id]::handleDeleteTopic::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_TOPIC_IS_LOADING, payload: false });
        }
    }




    return (
        <>
            <ErrorMessage errorMessage={state?.errorMessage} />
            <ForumHeader forum={state?.forum} />

            
            <div className="flex flex-col gap-1 mb-10">
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-3">
                        { state?.topic && (
                            <>
                                { state.topic.status == 'draft' && ( <span className="text-gray-500 border border-gray-500 bg-gray-100 rounded-lg px-2 text-xs">brouillon</span> ) }
                                <h1 className="text-2xl font-bold underline underline-offset-8 text-primary">{state?.topic?.title}</h1>
                            </>
                        )}
                    </div>
                    
                    <div className="flex flex-row items-center gap-2">
                        {
                            hasTopicModeratorRight && (
                                <button 
                                    className="text-sm text-red-200 hover:text-red-700" 
                                    onDoubleClick={() => handleDeleteTopic()}
                                    title="Double-cliquez pour supprimer le sujet"
                                >
                                        Supprimer le sujet
                                </button>
                            )
                        }
                        { state.isLoading && <BarLoader size={20} color="#696969" /> }
                    </div>
                </div>
            </div>
            

            { messages && state.topic && messages.filter((message) => message.status != 'draft')?.map((message, i) => {
                return ( 
                    <div key={message.id} className="flex flex-row items-start gap-2 mb-10">
                        { message.author.picture_url ? <img src={apiURL + message.author.picture_url} alt="Photo de l'utilisateur" className={`rounded-full w-14 h-14 border-2 ${message.author.is_online && "border-green-500 border-2 flex-shrink-0"}`} />
                            : <div className={`rounded-full bg-pink-400 w-14 h-14 text-xl flex items-center justify-center ${message.author.is_online && "border-green-500 border-2 flex-shrink-0"}`}>
                                {toInitials(message.author.first_name, message.author.last_name)}
                            </div> }

                        <div className="flex-auto flex flex-col" translate="no">
                            <div className="rounded-lg bg-gray-100">
                                <div className="flex flex-row justify-between items-center p-3">
                                    <span className="font-bold text-sm text-secondary">
                                        {message.author.first_name} {message.author.last_name}
                                    </span>
                                    <span className="text-sm text-gray-500 flex flex-row items-center gap-2">
                                        { 
                                            hasMessageWriteRight(message) && (
                                                state.draft !== null 
                                                    ? <span title="Quitter le brouillon pour éditer le message">
                                                        <PencilOff 
                                                            size={16} 
                                                            title=""
                                                        />
                                                    </span> 
                                                    : <Pencil 
                                                        size={16} 
                                                        className="cursor-pointer hover:text-red-500" 
                                                        onClick={() => { 
                                                            dispatch({type: ACTIONS.SET_DRAFT, payload: message}) 
                                                        }}
                                                        disabled={state.draft !== null}
                                                    />
                                            )
                                        }
                                        { message.edited 
                                            ? <span title={`Publié le ${dayjs(message.creation_date).format('lll')}, édité le ${dayjs(message.edition_date).format('lll')}`}>{dayjs(message.edition_date).fromNow()} (edité)</span>
                                            : <span title={dayjs(message.creation_date).format('lll')}>{dayjs(message.creation_date).fromNow()}</span> 
                                        }
                                        
                                    </span>
                                </div>
                                <MDXEditor 
                                    key={message.id}
                                    ref={(el) => messageRefs.current[message.id] = el}
                                    readOnly={true}
                                    markdown={message.content} 
                                    plugins={[
                                        headingsPlugin(), 
                                        linkPlugin(),
                                        quotePlugin(), 
                                        listsPlugin(), 
                                        thematicBreakPlugin(), 
                                        tablePlugin(),
                                        imagePlugin(),
                                        codeBlockPlugin({ defaultCodeBlockLanguage: 'raw' }),
                                        codeMirrorPlugin({ codeBlockLanguages: { 
                                            raw: 'RAW', c: 'C', css: 'CSS', cpp: 'C++', cs: 'C#', go: 'Go', html: 'HTML', 
                                            java: 'Java', js: 'JavaScript', json: 'JSON', md: 'Markdown', php: 'PHP', py: 'Python', rb: 'Ruby', 
                                            sql: 'SQL', ts: 'TypeScript', xml: 'XML' } }),
                                    ]} 
                                    contentEditableClassName='message'
                                />
                                { message?.attachments?.filter((attachment) => !attachment.inline).length > 0 && (
                                    <div className="flex flex-row gap-2 m-2 p-2 border-t border-gray-300 border-dashed">
                                        {message.attachments.filter((attachment) => !attachment.inline).map((attachment, i) => {
                                            return (
                                                <div key={i} className="flex flex-row items-center gap-2">
                                                    <div className="bg-white rounded-sm px-2 py-1 flex flex-row items-center justify-center gap-1">
                                                        <span className="text-gray-500 text-xs" translate="no">{attachment.name}</span>
                                                        <a href={`${apiURL}${attachment.url}`} className="text-primary hover:underline">
                                                            <Download size={16} className="text-primary" />
                                                        </a>
                                                        <span className="text-gray-500 text-xs italic" translate="no">{ (Number(attachment.size) / 1024 / 1024).toPrecision(2) } Mo</span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-row justify-end gap-1 mt-1 flex-wrap">
                                { i == messages.length - 1 
                                    ? state.topic.views.filter((view) => dayjs(view.view_date) >= dayjs(message.creation_date)).map((view, i) => {
                                        return (
                                            view.user.picture_url ? <img src={apiURL + view.user.picture_url} title={`${view.user.first_name} ${view.user.last_name}`} className={`rounded-full w-5 h-5 border-2 ${message.author.is_online && "border-green-500 border-2"}`} />
                                            : <div key={i} className="rounded-full bg-pink-300 text-[8px] w-5 h-5 p-1 text-center flex items-center justify-center" 
                                                title={`${view.user.first_name} ${view.user.last_name}, le ${dayjs(view.view_date).format('lll')}`}
                                                translate="no"
                                            >
                                                {toInitials(view.user.first_name,view.user.last_name)}
                                            </div>
                                        )
                                    })
                                    : state.topic.views.filter((view) => 
                                            ( dayjs(view.view_date) >= dayjs(message.creation_date) && dayjs(view.view_date) < dayjs(messages[i+1].creation_date) )
                                        ).map((view, i) => {
                                        return (
                                            view.user.picture_url ? <img src={apiURL + view.user.picture_url} title={`${view.user.first_name} ${view.user.last_name}`} className={`rounded-full w-5 h-5 border-2 ${message.author.is_online && "border-green-500 border-2"}`} />
                                            : <div key={i} className="rounded-full bg-pink-300 text-[8px] w-5 h-5 p-1 text-center flex items-center justify-center" 
                                                title={`${view.user.first_name} ${view.user.last_name}, le ${dayjs(view.view_date).format('lll')}`}
                                                translate="no"
                                            >
                                                {toInitials(view.user.first_name,view.user.last_name)}
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            <div className="flex flex-row text-center justify-center text-orange-300 italic text-sm">
                                {
                                    (i < messages.length - 1) && (state.topic.views.filter((view) => {
                                        // console.log(view.view_date, dayjs(message.creation_date), dayjs(messages[i+1].creation_date), view.user_id, auth.user.id)
                                        return ( dayjs(view.view_date) >= dayjs(message.creation_date) && dayjs(view.view_date) < dayjs(messages[i+1].creation_date) && view.user_id == auth.user.id )
                                    }).length > 0) && (
                                        <span>------ Votre dernière visite s&apos;est terminée ici ------</span>
                                    )
                                }
                            </div>
                        </div>
                    </div>  
                 )
            })}
            
            { state.draft && (
                <div className="flex flex-row items-start gap-2 mb-10">
                    { state.draft.author.picture_url ? <img src={apiURL + state.draft.author.picture_url} alt="Photo de l'utilisateur" className={`rounded-full w-14 h-14 border-2 ${state.draft.author.is_online && "border-green-500 border-2"}`} />
                            : <div className={`rounded-full bg-pink-400 w-14 h-14 text-xl flex items-center justify-center ${state.draft.author.is_online && "border-green-500 border-2"}`}>
                                {toInitials(state.draft.author.first_name, state.draft.author.last_name)}
                            </div> }

                    <div className="rounded-lg bg-gray-100 flex-auto flex flex-col">
                        <div className="flex flex-row justify-between items-center p-3">
                            <span className="font-bold text-sm text-secondary" translate="no">{state.draft.author.first_name} {state.draft.author.last_name}</span>
                        </div>
                        <div className="m-2 bg-white rounded-md border border-gray-400" translate="no">
                            <MDXEditor 
                                ref={editorRef}
                                key={state.draft.id}
                                markdown={state.draft.content} 
                                plugins={[
                                    headingsPlugin(), 
                                    linkPlugin(), linkDialogPlugin(),
                                    quotePlugin(), 
                                    listsPlugin(), 
                                    thematicBreakPlugin(), 
                                    tablePlugin(),
                                    markdownShortcutPlugin(),
                                    toolbarPlugin({
                                        toolbarClassName: "flex-wrap",
                                        toolbarContents: () => (
                                            <DiffSourceToggleWrapper>
                                                <ConditionalContents
                                                    options={[
                                                        { when: (editor) => editor?.editorType === 'codeblock', contents: () => <ChangeCodeMirrorLanguage /> },
                                                        {
                                                        fallback: () => (
                                                            <>
                                                            <UndoRedo />
                                                            <Separator />

                                                            <BoldItalicUnderlineToggles />
                                                            
                                                            <Separator />
                                                            
                                                            <StrikeThroughSupSubToggles />
                                                            <Separator />

                                                            <ListsToggle />
                                                            <Separator />

                                                            <BlockTypeSelect />
                                                            <Separator />

                                                            <CreateLink />
                                                            <InsertImage />
                                                            <Separator />

                                                            <InsertTable />
                                                            <InsertThematicBreak />
                                                            <Separator />

                                                            <CodeToggle />
                                                            <InsertCodeBlock />
                                                            </>
                                                        )
                                                        }
                                                    ]}
                                                    />
                                                
                                            </DiffSourceToggleWrapper>
                                        )
                                    }),
                                    diffSourcePlugin({
                                        diffMarkdown: state.draft.content,
                                        viewMode: 'rich-text',
                                        readOnlyDiff: true
                                    }),
                                    imagePlugin({
                                        imageUploadHandler
                                    }),
                                    codeBlockPlugin({ defaultCodeBlockLanguage: 'raw' }),
                                    codeMirrorPlugin({ codeBlockLanguages: { 
                                        raw: 'RAW', c: 'C', css: 'CSS', cpp: 'C++', cs: 'C#', go: 'Go', html: 'HTML', 
                                        java: 'Java', js: 'JavaScript', json: 'JSON', md: 'Markdown', php: 'PHP', py: 'Python', rb: 'Ruby', 
                                        sql: 'SQL', ts: 'TypeScript', xml: 'XML' } }),
                                ]} 
                                contentEditableClassName='message'
                            />
                        </div>
                        <div className="flex justify-between flex-row gap-3 my-4 mx-2 items-center">
                            <div className="flex flex-auto flex-col gap-2 items-center">
                                <div className="flex flex-row gap-2 items-stretch justify-between 
                                    w-full border border-dashed border-gray-400 rounded-md p-2 hover:border-gray-800"
                                >

                                    {/* File Upload Form */}
                                    <input 
                                        type="file" 
                                        onChange={async (e) => {
                                                if (e.target.files.length > 0) {
                                                        await fileUploadHandler(e.target.files[0]);
                                                        e.target.value = null;
                                                }
                                        }} 
                                        className="text-sm"
                                    />

                                    {/* Drag and Drop Area */}
                                    <div 
                                        className="flex-auto text-center rounded-md text-secondary text-xs items-center flex justify-center" 
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={async (e) => {
                                                e.preventDefault();
                                                if (e.dataTransfer.files.length > 0) {
                                                        await fileUploadHandler(e.dataTransfer.files[0]);
                                                }
                                        }}
                                    >
                                            Déposer des fichiers ici (max 20 Mo)
                                    </div>
                                </div>
                                <div className="flex flex-row gap-2 w-full flex-wrap">
                                    {/* Display Attachments */}
                                    {state.draft?.attachments?.map((attachment, i) => (
                                        <div key={i} className="flex flex-row items-center gap-2">
                                            <div className="bg-white rounded-sm px-2 py-1 flex flex-row items-center justify-center gap-1">
                                                <span className="cursor-pointer text-red-500 text-xs" onClick={() => handleDeleteFile(attachment.uuid)}>
                                                    <Trash size={16}/>
                                                </span>
                                                <span className="text-gray-500 text-xs">{attachment.name}</span>
                                                <a href={`${apiURL}${attachment.url}`} className="text-primary hover:underline">
                                                    <Download size={16} className="text-primary" />
                                                </a>
                                                <span className="text-gray-500 text-xs italic">{ (Number(attachment.size) / 1024 / 1024).toPrecision(2) } Mo</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex flex-row gap-2 justify-end w-full items-center">
                                    { state?.draftHandlerMessage?.type != "" && state?.draftHandlerMessage?.message != "" && (
                                        state?.draftHandlerMessage?.type == "success" 
                                            ? <div className="text-green-600 flex flex-row items-center gap-2"><CheckCircle size={16} />{state.draftHandlerMessage.message}</div>
                                            : state?.draftHandlerMessage?.type == "info"
                                                ? <div className="text-gray-600 flex flex-row items-center gap-2"><Info size={16} />{state.draftHandlerMessage.message}</div>
                                                : state?.draftHandlerMessage?.type == "error"
                                                    ? <div className="text-red-600 flex flex-row items-center gap-2"><CircleX size={16} />{state.draftHandlerMessage.message}</div>
                                                    : <div className="text-gray-600 flex flex-row items-center gap-2">{state.draftHandlerMessage.message}</div>
                                    ) }

                                    { (state.updateIsLoading) && (
                                        <PulseLoader size={6} color="#222" />
                                    ) }
                                    { (state.uploadIsLoading) && (
                                        <div className="flex flex-row items-center gap-1">
                                            <span className="text-gray-500 text-xs">
                                                {progress}%
                                            </span>
                                            <PulseLoader size={6} color="#222" />
                                        </div>
                                    ) }
                                    { (state.draft.status == 'draft' || hasTopicModeratorRight) && (
                                        <button 
                                            className="bg-red-50 hover:bg-red-200 text-secondary border border-red-300 rounded-md px-2 py-1 text-sm" 
                                            onClick={() => handleDeleteDraft()}
                                            disabled={state.draftHandlerMessage.message || state.updateIsLoading || state.uploadIsLoading}
                                        >
                                                Supprimer le {state.draft?.status == "draft" ? "brouillon" : "message"}
                                        </button>
                                    )}
                                    { state.draft?.status == "draft" && (
                                        <>
                                            <button 
                                                className="bg-gray-50 hover:bg-gray-200 text-secondary border border-gray-300 rounded-md px-2 py-1 text-sm" 
                                                onClick={() => handleSaveDraft()}
                                                disabled={state.draftHandlerMessage.message || state.updateIsLoading || state.uploadIsLoading}
                                            >
                                                    Enregistrer {state.draft?.status == "draft" ? "le brouillon" : "le message"}
                                            </button>
                                            <button
                                                className="bg-green-200 hover:bg-green-500 text-primary border border-green-300 rounded-md px-2 py-1 text-sm" 
                                                onClick={() => handlePublishDraft()}
                                                disabled={state.draftHandlerMessage.message || state.updateIsLoading || state.uploadIsLoading}
                                            >
                                                    Enregistrer et publier
                                            </button>
                                        </>
                                    )}
                                    { state.draft?.status != "draft" && (
                                        <>
                                            <button
                                                className="bg-gray-50 hover:bg-gray-200 text-primary border border-gray-300 rounded-md px-2 py-1 text-sm" 
                                                onClick={() => dispatch({type: ACTIONS.SET_DRAFT, payload: null})}
                                                disabled={state.draftHandlerMessage.message || state.updateIsLoading || state.uploadIsLoading}
                                            >
                                                    Annuler la modification
                                            </button>
                                            <button
                                                className="bg-green-200 hover:bg-green-500 text-primary border border-green-300 rounded-md px-2 py-1 text-sm" 
                                                onClick={() => handleSaveMessage()}
                                                disabled={state.draftHandlerMessage.message || state.updateIsLoading || state.uploadIsLoading}
                                            >
                                                    Enregistrer le message
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div ref={editorDivRef}></div>

            { messages && hasTopicWriteRight && !state.draft && !state.updateIsLoading && (
                <div className="flex flex-row justify-end">
                    <button 
                        className="hover:bg-gray-200 border border-gray-500 rounded-sm px-2 py-1 text-base flex flex-0 gap-2 items-center" 
                        onClick={() => newDraft()}>
                            <CornerDownLeft size={16} /> Répondre 
                    </button>
                </div>
            )}

            { messages && !hasTopicWriteRight && !state.updateIsLoading && (
                state?.topic?.no_response ? (
                    <div className="flex flex-row justify-end">
                        <span className="text-gray-500 text-sm italic">Ce sujet n&apos;autorise pas de réponse</span>
                    </div>
                ) : (
                    <div className="flex flex-row justify-end">
                        <span className="text-gray-500 text-sm italic">Vous n&apos;avez pas le droit de répondre à ce sujet</span>
                    </div>
                )
            )}
        </>
    )
}